import { useCallback, useEffect, useRef, useState } from 'react'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import {
  useCreateSessionMutation,
  useLazyGetAvailableSlotsQuery,
  useBookSlotMutation,
  type AvailableSlotsResponse,
  type BookSlotResponse
} from './api/bookingApi'

/**
 * Drives the client booking session against the enforced API:
 *  1. Exchanges the long-lived link token (from the email ?token=) for a short-lived
 *     session token on load — kept in memory only, never persisted.
 *  2. Loads available slots with the session token; each read issues/rotates a nonce.
 *  3. Books with the current nonce; on success rotates to the nonce the write returns
 *     (so reschedule works without re-reading).
 *  4. Recovers automatically: 401 (session expired) → re-exchange; 409 STALE_NONCE →
 *     refresh the nonce via a fresh read. One retry each. A 409 SLOT_TAKEN (another
 *     circle booked the slot first) refreshes the list and surfaces the error instead
 *     of retrying.
 *
 * The session token and nonce live in refs (in-memory) — not localStorage/sessionStorage —
 * so they die with the tab.
 */
export function useBookingSession(linkToken: string | null) {
  const [createSession] = useCreateSessionMutation()
  const [fetchSlots] = useLazyGetAvailableSlotsQuery()
  const [bookSlotMut] = useBookSlotMutation()

  const sessionTokenRef = useRef<string | null>(null)
  const nonceRef = useRef<string | null>(null)

  const [slotsData, setSlotsData] = useState<AvailableSlotsResponse | undefined>()
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<unknown>()

  // The session/slots flow is deferred until the user clicks Continue (start()), so the
  // short-lived session clock begins when they actually begin booking — not on page load.
  const [started, setStarted] = useState(false)
  const start = useCallback(() => setStarted(true), [])

  const [booking, setBooking] = useState(false)
  const [bookResult, setBookResult] = useState<BookSlotResponse | undefined>()
  const [bookError, setBookError] = useState<unknown>()

  // Ensure a valid session token exists, exchanging the link token if needed.
  const ensureSession = useCallback(async (): Promise<string> => {
    if (sessionTokenRef.current) return sessionTokenRef.current
    if (!linkToken) {
      throw new Error('No booking token')
    }
    const session = await createSession(linkToken).unwrap()
    sessionTokenRef.current = session.sessionToken
    // The nonce is issued by the first available-slots read, not by /session.
    return session.sessionToken
  }, [linkToken, createSession])

  // Fetch slots (forced, never cached — the read rotates the nonce server-side).
  const loadSlots = useCallback(async (): Promise<void> => {
    const sessionToken = await ensureSession()
    const data = await fetchSlots(sessionToken, false).unwrap()
    nonceRef.current = data.bookingNonce
    setSlotsData(data)
  }, [ensureSession, fetchSlots])

  // On start (Continue click): exchange session + fetch slots.
  useEffect(() => {
    if (!linkToken || !started) return
    let cancelled = false
    setLoading(true)
    setError(undefined)
    loadSlots()
      .catch((e) => {
        if (!cancelled) setError(e)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [linkToken, started, loadSlots])

  const attemptBook = useCallback(
    async (date: string, time: string, mode: string): Promise<BookSlotResponse> => {
      const sessionToken = await ensureSession()
      const res = await bookSlotMut({
        sessionToken,
        bookingNonce: nonceRef.current ?? '',
        selectedDate: date,
        selectedStartTime: time,
        meetingMode: mode
      }).unwrap()
      // A successful booking ends the session — the API returns no nonce, so clear ours.
      // To modify, the client reopens the link (a fresh session + nonce).
      nonceRef.current = null
      return res
    },
    [ensureSession, bookSlotMut]
  )

  const book = useCallback(
    async (date: string, time: string, mode: string): Promise<void> => {
      setBooking(true)
      setBookError(undefined)
      try {
        let res: BookSlotResponse
        try {
          res = await attemptBook(date, time, mode)
        } catch (err) {
          const status = (err as FetchBaseQueryError)?.status
          if (status === 401) {
            // Session expired — re-exchange, refresh slots + nonce, retry once.
            sessionTokenRef.current = null
            nonceRef.current = null
            await loadSlots()
            res = await attemptBook(date, time, mode)
          } else if (status === 409) {
            const code = (err as FetchBaseQueryError & { data?: { error?: string } })
              ?.data?.error
            if (code === 'SLOT_TAKEN') {
              // Another circle grabbed this slot between our read and write. Refresh the
              // slot list (drops the taken slot + issues a fresh nonce) and surface the
              // error so the user reselects — do NOT retry the same slot.
              await loadSlots()
              throw err
            }
            // STALE_NONCE — refresh it via a fresh read, retry once.
            await loadSlots()
            res = await attemptBook(date, time, mode)
          } else {
            throw err
          }
        }
        setBookResult(res)
      } catch (err) {
        setBookError(err)
      } finally {
        setBooking(false)
      }
    },
    [attemptBook, loadSlots]
  )

  return { slotsData, loading, error, booking, bookResult, bookError, book, start, started }
}
